<template>
	<div :class="['dropdown__list-container', showList ? 'is-open' : '']">
		<Search
			:placeholder="placeholder"
			ref="autosuggestion"
			:id="`${this.uid}_sugg_search`"
			:timeout="0"
			@change="search"
			v-model="searchText"
			v-if="autosuggestion && showList"
		/>
		<ul
			ref="dropdownlist"
			class="dropdown-list"
			:id="uid"
			:name="uname"
			:variant="variant"
		>
			<li
				ref="options"
				v-for="(item, index) in items"
				:id="`${uid}_${index}`"
				:class="[
					'dropdown-list__item',
					item.selected ? 'is-active' : '',
					index === currentItem ? 'selected' : '',
					item.disabled ? 'is-disabled' : ''
				]"
				:tabindex="index"
				:key="index"
				@click="clickHandler(item, index, $event)"
				@keydown.enter="changeItem(item)"
				@keydown.up.prevent="nextItem"
				@keydown.down.prevent="nextItem"
			>
				<slot name="item-content">
					<div v-if="multiSelect" class="list-item">
						<Checkbox
							:checked="item.selected"
							:disabled="item.disabled"
							:id="`${uid}_${index}_cb`"
							:labelText="item.text"
							:variant="variant"
							v-model="item.selected"
							@change="changeItem(item, $event)"
						/>
					</div>
					<div v-if="zone && !multiSelect" class="list-item">
						<span class="list-item__zone">
							{{ item.text }}
						</span>
					</div>
					<div v-if="!zone && !multiSelect" class="list-item">
						<a
							href="javascript:void(0)"
							class="list-item__link"
							tabindex="-1"
						>
							{{ item.text }}
						</a>
					</div>
					<div v-if="multiSelect" class="list-item">
						<Divider class="dropdown-list__divider" />
					</div>
				</slot>
			</li>
		</ul>
	</div>
</template>

<script>
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox';
import Search from '@/components/forms/search/search';
import { isArray } from '@/utils/inspect';
import Divider from '@/components/shells/divider/divider';

export default {
	name: 'DropdownList',
	model: {
		prop: 'value',
		event: 'change'
	},
	components: {
		Checkbox,
		Search,
		Divider
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		value: {
			// type: [String, Number, Array],
			default: null
		},
		options: {
			type: Array,
			required: true
		},
		multiSelect: {
			type: Boolean,
			default: false
		},
		zone: {
			type: Boolean,
			default: false
		},
		autosuggestion: {
			type: Boolean,
			default: false
		},
		minLen: {
			type: Number,
			default: 1
		},
		placeholder: {
			type: String,
			default: 'Search'
		}
	},
	data() {
		return {
			selected: null,
			showList: false,
			items: [],
			searchText: '',
			currentItem: -1,
			isMounted: false
		};
	},
	computed: {
		existsValueOnItems() {
			return (
				this.items && this.items.some(item => item.value === this.value)
			);
		}
	},
	mounted() {
		this.isMounted = true;
	},
	destroyed() {
		this.isMounted = false;
	},
	methods: {
		clickHandler(item, index) {
			if (this.multiSelect) {
				const checkbox = this.$refs.options[index].querySelector(
					'input[type="checkbox"]'
				);
				if (
					checkbox &&
					(event.target === checkbox ||
						event.target.tagName === 'LABEL')
				) {
					// Si el usuario hizo clic en el checkbox o en el label, no necesitas realizar ninguna acción adicional.
					// El cambio de estado se manejará automáticamente a través del navegador.
				} else {
					// Si el usuario hizo clic en cualquier otro lugar del elemento, activa el cambio de estado manualmente.
					checkbox.click();
				}
			} else {
				this.changeItem(item);
			}
		},
		nextItem(event) {
			const upArrow = 38;
			const downArrow = 40;
			if (event.keyCode == upArrow && this.currentItem > 0) {
				this.currentItem--;
				this.$refs.dropdownlist.childNodes[this.currentItem].focus();
				this.fixScrolling();
			} else if (
				event.keyCode == downArrow &&
				this.currentItem < this.items.length - 1
			) {
				this.currentItem++;
				this.$refs.dropdownlist.childNodes[this.currentItem].focus();
				this.fixScrolling();
			}
		},
		changeItem(item, value) {
			if (this.multiSelect) {
				this.updateSelectedValue(item, value);
			} else {
				this.resetSelectedItems();
				item = this.updateSelectedValue(item);
				this.searchText = '';
				this.$emit('change', item.value);
			}
		},
		resetSelectedItems() {
			this.items.forEach(item => (item.selected = false));
			this.selected = [];
		},
		getValueSelectedItems() {
			this.selected = this.items
				.filter(i => i.selected)
				.map(item => item.value);
		},
		updateSelectedValue(item, val) {
			if (val !== undefined) {
				item.selected = !val;
			}
			const idx = this.items.indexOf(item);
			item.selected = !item.selected;
			this.items[idx] = item;
			return item;
		},
		updateSelectedValueItems(value, force) {
			if (
				force ||
				JSON.stringify(value) != JSON.stringify(this.selected)
			) {
				//reset selected value in items
				this.resetSelectedItems(null);

				if (value || this.existsValueOnItems) {
					const values = isArray(value) ? value : [value];
					this.items
						.filter(item => values.some(val => val === item.value))
						.forEach(item => (item.selected = true));
					this.selected = value;
				} else {
					this.searchText = '';
				}
			}
		},
		fixScrolling() {
			const liH = this.$refs.options[this.currentItem].clientHeight;
			this.$refs.dropdownlist.scrollTop = liH * this.currentItem;
		},
		search(value, event) {
			if (event?.keyCode === 13 && this.currentItem > -1) {
				const item = this.items[this.currentItem];
				if (item) {
					this.searchText = '';
					this.$emit('change', item.value);
				}
			}
		}
	},
	watch: {
		options: {
			immediate: true,
			handler(val) {
				this.items = [...val];
				this.updateSelectedValueItems(this.value, true);
			}
		},
		isOpen: {
			immediate: true,
			handler(val) {
				if (this.isMounted && val && this.options.length) {
					this.$nextTick(function() {
						if (this.$refs.dropdownlist.firstElementChild) {
							this.$refs.dropdownlist.firstElementChild.focus();
						}
					});
				}
				this.showList = val;
				if (val) {
					if (
						this.selected == this.value &&
						!this.items.some(item => item.selected)
					) {
						this.updateSelectedValueItems(this.value, true);
					}

					if (this.$refs.autosuggestion) {
						this.$nextTick(() =>
							this.$refs.autosuggestion.$refs.search.focus()
						);
						this.currentItem = -1;
					}
				}

				//when close the list and multiselect is true
				//emit the value of selected items
				if (!val && this.multiSelect) {
					this.getValueSelectedItems();
					this.searchText = '';
					this.$emit('change', this.selected);
				}
			}
		},
		value: {
			handler(value) {
				this.updateSelectedValueItems(value);
			}
		},
		searchText: {
			handler(text) {
				if (this.autosuggestion && text) {
					text = (text || '').toString().toLowerCase();
					this.items = this.items.filter(item =>
						(item.text || '')
							.toString()
							.toLowerCase()
							.includes(text)
					);
				} else {
					this.items = [...this.options];
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';

/*------------------------------------*\
  #DROPDOWN LIST with <ul> and <li>
\*------------------------------------*/

.dropdown__list-container {
	// top: 100%;
	// left: 0;
	// position: absolute;
	z-index: 999;
	width: max-content;
	min-width: 100%;
	@include stylized-dropdown;
	display: none;

	&.is-open {
		display: block;
	}
}

.dropdown-list {
	padding-inline-start: 0;
	margin-block-start: 2px;
	margin-block-end: 0;
	@include body-text('regular');
	line-height: 1;
	max-height: 200px; // display scroll if there's more than 10 items
	overflow-y: auto;
	@include scrollbar('small');
	padding: $padding-sm 0;

	&__item {
		&:active,
		&:focus {
			::v-deep .checkbox__input {
				border: $form-input-border;
				background-color: $form-input-bkgd;

				&::before {
					content: '';
					@include vertical-horizontal-center-with-transform;
					background-color: none;
					width: 24px;
					height: 24px;
					border: $form-input-border--focus;
					border-radius: $border-radius;
				}
			}
		}

		&:checked {
			border: $form-input-border--checked;
		}

		&:focus-visible {
			outline: none;
		}

		cursor: pointer;
		padding: $padding-sm;

		&.is-active {
			background: $neutral-4;
			@include text-contrast($neutral-4, $white, $black);
		}

		&:hover,
		&.selected {
			background: lighten($neutral-4, 5%);
			@include text-contrast(lighten($neutral-4, 5%), $white, $black);
		}

		&.disabled {
			pointer-events: none;
			opacity: $disabled-opacity;
		}

		.list-item {
			display: inline-flex;
			width: 100%;

			&__link {
				word-break: break-all;
			}
		}

		// These styles are for the color swatchs on the "zone" varient

		$colors: $ui-secondary-2-light $ui-secondary-4-light
			$ui-secondary-3-light $ui-secondary-1-light $ui-neutral-light;

		@for $i from 1 to length($colors) {
			&:nth-child(#{$i + 2}) {
				.list-item__zone {
					&::before {
						content: '';
						position: relative;
						display: inline-block;
						bottom: -2px;
						margin-right: $gutter-xs;
						height: 15px;
						width: 15px;
						border: $form-input-border;
						border-radius: 2px;
						margin-right: $gutter-md;
						$color-from-list: nth($colors, $i);
						background-color: $color-from-list;
					}
				}
			}
		}

		&__divider {
			width: 100%;
		}
	}
}
.search {
	padding: $padding-sm $padding-lg 0 $padding-sm;
}
</style>
